/* Built In Imports */
import NextLink from 'next/link';
import { useRouter } from 'next/router';

/* External Imports */
import { Box, Link } from '@chakra-ui/react';
import { nanoid } from 'nanoid';

/* Internal Imports */
/* Components */
import LazyLoadImageComponent from '@components/UI/LazyLoadImageComponent';
import { refineUrlDomain } from '@components/Utility/utils';
import config from '@config';

/* Styles */
import classes from '@styles/components/CardFilter.module.css';

/**
 * Renders the MMM Card component
 *
 * @param {object} mmmCardData - data
 * @param {string} lang language
 * @returns {ReactElement} MMMCard component
 */

const MMMCard = ({ mmmCardData, lang, region }) => {
  const mmmTypes = {
    article: '#080485',
    video: '#ae4511',
    podcast: '#00334a',
    poem: '#676055',
    ebook: '#0d5878',
  };
  const router = useRouter();
  const currentUrl = router.asPath.includes('sadhguru/mission');

  const trimText = text => {
    return text.length > 60 ? text.substring(0, 85) + '...' : text;
  };

  return (
    <Box
      m="0 auto"
      padding={{ base: '20px 15px 0', md: '35px 30px', lg: '15px 1px' }}
      display="flex"
      flexFlow="row wrap"
    >
      {mmmCardData?.map((post, index) => {
        const alternateClass = index % 2 === 0 ? classes.even : classes.odd;
        const mmmTag = post.mmmTag.toLowerCase();
        const articleUrl = post.url.replace(
          config.cdnPath,
          // 'https://isha.sadhguru.org/',
          config.BASE_PATH
        );
        return (
          <Box
            key={nanoid()}
            float="left"
            w={{
              base: '100%',
              md: 'calc((100% - 60px) / 2)',
              sm: '100%',
              lg: 'calc((100% - 90px) / 4)',
            }}
            mr={(index + 1) % 4 === 0 ? '0' : { base: '0', md: '30px' }}
            mb={{ base: '20px', md: '3rem', sm: '1rem', lg: '20px' }}
            _hover={{ boxShadow: '0 0 48px 0 rgba(0, 0, 0, 0.25)' }}
          >
            <Box
              float="left"
              w="100%"
              backgroundColor="#ffffff"
              pb="1rem"
              boxShadow="0 0 16px 0 rgb(0 0 0 / 15%)"
              minH="308px"
              cursor="pointer"
            >
              <Box
                float="left"
                w="100%"
                pos="relative"
                h="auto"
                mb="0.5rem"
                zIndex="0"
              >
                <Box
                  fontFamily="FedraSansStd-A-medium"
                  h="26.2px"
                  fontWeight="500"
                  lineHeight="22px"
                  pos="absolute"
                  zIndex="1"
                  float="right"
                  right="0"
                  w="70%"
                  maxWidth="100px"
                  textShadow="rgb(255 255 255 / 50%) 0px 1px 0px"
                  p="0px 16px !important"
                  fontSize="12px"
                  textAlign="right"
                  clipPath="polygon(0 -190%, 100% 0, 100% 100%, 20% 100%)"
                  color="#fff"
                  boxShadow="0px 0px 3px rgb(0 0 0 / 30%)"
                  bgColor={mmmTypes[mmmTag]}
                >
                  {post.mmmTag}
                </Box>
                <Box
                  minH="163px"
                  //  bgGradient="linear-gradient(to right, #e4ded4 , #ffffff);"
                >
                  <Box as="span">
                    <NextLink
                      href={refineUrlDomain(articleUrl, region, lang)}
                      prefetch={false}
                      passHref
                      legacyBehavior
                    >
                      <Link target={currentUrl == true ? '_blank' : ''}>
                        <LazyLoadImageComponent
                          clipPath={
                            alternateClass === classes.odd
                              ? 'polygon(0 0, 100% 0, 100% 92%, 0 100%)'
                              : 'polygon(0 0, 100% 0, 100% 100%, 0% 92%);'
                          }
                          width="100%"
                          src={post.cardImage && post.cardImage?.url}
                          alt={post.cardImage && post.cardImage?.alt}
                          title={post.cardImage && post.cardImage?.title}
                          minH="163px"
                          bgColor="#EEEEEE"
                        />
                      </Link>
                    </NextLink>
                  </Box>
                </Box>
                <NextLink
                  href={refineUrlDomain(articleUrl, region, lang)}
                  prefetch={false}
                  passHref
                  legacyBehavior
                >
                  <Link target={currentUrl == true ? '_blank' : ''}>
                    <Box
                      float="left"
                      fontFamily="FedraSansStd-A-medium"
                      fontSize="14px"
                      lineHeight="1.25"
                      textAlign="left"
                      color="#28231e"
                      p="0.5rem 1.5rem 0 1.5rem"
                      minHeight="65px !important"
                      display="flex"
                      fontWeight={lang !== 'en' && '600'}
                      alignItems="center"
                    >
                      {post.title}
                    </Box>
                  </Link>
                </NextLink>
              </Box>
              <NextLink
                href={refineUrlDomain(articleUrl, region, lang)}
                prefetch={false}
                passHref
                legacyBehavior
              >
                <Link target={currentUrl == true ? '_blank' : ''}>
                  <Box
                    float="left"
                    fontFamily="FedraSansStd-book"
                    fontSize="14px"
                    color="#676055 !important"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    d="-webkit-box"
                    lineHeight="20px !important"
                    p="0 1.5rem"
                    className="card-content"
                  >
                    {trimText(post.summary)}
                  </Box>
                </Link>
              </NextLink>
              <style jsx global>{`
                .card-content {
                  -webkit-line-clamp: 3;
                  -webkit-box-orient: vertical;
                }
              `}</style>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default MMMCard;
